/**
 * ============================================================================
 * SERVICE: Tiny URL & Redirection Management Platform
 * ============================================================================
 * Purpose:
 *   Handles API requests for Tiny URLs, telemetry analytics, and click logs.
 * ============================================================================
 */

import api from "../../core/services/api";

const tinyUrlService = {
  /**
   * Retrieves paginated list of links with optional status, search, and sorting.
   * @param {Object} [params] - { page, limit, search, status, sortBy, accountFilter }
   */
  list: (params = {}) => {
    return api.get("/tiny-urls", { params });
  },

  /**
   * Alias for list
   */
  get: (params = {}) => {
    return api.get("/tiny-urls", { params });
  },

  /**
   * Creates a new tiny URL.
   * @param {Object} data - { title, description, originalDestinationUrl, expirationPreset, expirationDate, customCode }
   */
  create: (data) => {
    return api.post("/tiny-urls", data);
  },

  /**
   * Bulk creates multiple tiny URLs simultaneously.
   * @param {Object} data - { items: Array<{ title, originalDestinationUrl, customCode }>, expirationPreset, expirationDate, description }
   */
  bulkCreate: (data) => {
    return api.post("/tiny-urls/bulk", data);
  },

  /**
   * Retrieves a single link by ID or code.
   * @param {string} id - Mongo ID or 6-char code
   */
  getById: (id) => {
    return api.get(`/tiny-urls/${id}`);
  },

  /**
   * Updates an existing tiny URL (title, description, destination URL, expiry). Code remains immutable.
   * @param {string} id - Mongo ID or 6-char code
   * @param {Object} data - Updated fields
   */
  update: (id, data) => {
    return api.put(`/tiny-urls/${id}`, data);
  },

  /**
   * Toggles link active/inactive status.
   * @param {string} id
   * @param {boolean} [isActive]
   */
  toggleStatus: (id, isActive) => {
    return api.patch(`/tiny-urls/${id}/status`, { isActive });
  },

  /**
   * Soft-deletes a tiny URL.
   * @param {string} id
   */
  delete: (id) => {
    return api.delete(`/tiny-urls/${id}`);
  },

  /**
   * Retrieves detailed analytics (clicks over time, devices, browsers, referrers, locations).
   * @param {string} id
   */
  getAnalytics: (id) => {
    return api.get(`/tiny-urls/${id}/analytics`);
  },

  /**
   * Retrieves paginated click logs with masked IPs.
   * @param {string} id
   * @param {Object} [params] - { page, limit }
   */
  getClicks: (id, params = {}) => {
    return api.get(`/tiny-urls/${id}/clicks`, { params });
  },

  // ─── Bulk Phone Campaigns (Anti-Tamper) ───
  /**
   * Retrieves paginated list of Bulk Phone Campaigns.
   * @param {Object} [params] - { page, limit, search, status }
   */
  getCampaigns: (params = {}) => {
    return api.get("/tiny-urls/campaigns", { params });
  },

  /**
   * Retrieves a single campaign with contacts and click tracking.
   * @param {string} id - Mongo ID or 6-char code
   * @param {Object} [params] - { search, filter, page, limit }
   */
  getCampaignById: (id, params = {}) => {
    return api.get(`/tiny-urls/campaigns/${id}`, { params });
  },

  /**
   * Creates a new Bulk Phone Campaign with encrypted tokens per contact.
   * @param {Object} data - { title, description, originalDestinationUrl, expirationPreset, expirationDate, customCode, contacts: Array<{ phoneNumber, name, customCode }> }
   */
  createCampaign: (data) => {
    return api.post("/tiny-urls/campaigns", data);
  },

  /**
   * Deletes a bulk phone campaign.
   * @param {string} id
   */
  deleteCampaign: (id) => {
    return api.delete(`/tiny-urls/campaigns/${id}`);
  },

  /**
   * Toggles campaign status.
   * @param {string} id
   * @param {boolean} [isActive]
   */
  toggleCampaignStatus: (id, isActive) => {
    return api.patch(`/tiny-urls/campaigns/${id}/status`, { isActive });
  },
};

export default tinyUrlService;
